import { defineStore } from 'pinia';
import { ref } from 'vue';

import { TReachedResponse } from '@/api/types';
import {
  TAuthenticateContractorUserPayload,
  TAuthenticateUserPayload,
  TRegisterUserPayload,
  TSessionWithTokensSerialized,
  TUserResponse,
} from '@/contexts/accountingContext/domain/types';
import {
  authenticateUser,
  fetchUser,
  reauthenticateUser,
  registerUser,
  registerUserBySberBusinessAccount,
} from '@/contexts/accountingContext/services';
import { decodeJWT } from '@/utils';
import { showErrorNotification } from '@/domains/showErrorNotification';
import { EAppState, EWasAuthBy } from '@/constants';
import { TErrorNotification } from '@/types';
import { useAppStore } from '@/stores/app';

import { TDecodedAccessToken, TUserStore } from './types';

export const useUserStore = defineStore(
  'TMS_USER',
  (): TUserStore => {
    const isUserLoading = ref<boolean>(false);
    const user = ref<TUserResponse | null>(null);
    const sessionData = ref<TSessionWithTokensSerialized | null>(null);
    const decodedAccessToken = ref<TDecodedAccessToken>(null);
    const wasAuthBy = ref<EWasAuthBy | null>(null);

    /** Загрузить данные пользователя */
    const loadUser = async (withAppStateSetting: boolean = true) => {
      isUserLoading.value = true;

      try {
        const response: TReachedResponse<TUserResponse> = await fetchUser();
        if (response?.data) {
          user.value = response.data;
          if (withAppStateSetting) {
            const appStore = useAppStore();
            const isUserInteractionStateLocked = user.value?.oneTimePassword || user.value?.lockedSince
              ? EAppState.interactionLocked
              : EAppState.authenticated;
            appStore.setAppState(isUserInteractionStateLocked);
          }
        }
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        isUserLoading.value = false;
      }
    };

    /** Продолжить аутентификацию */
    const proceedSignIn = async (
      tokenData: TSessionWithTokensSerialized,
      authBy: EWasAuthBy,
    ) => {
      try {
        sessionData.value = { ...tokenData };
        wasAuthBy.value = authBy;

        if (!wasAuthBy.value || !sessionData.value) throw new Error('No session data');

        const appStore = useAppStore();
        appStore.setWasAuthBy(wasAuthBy.value);
        appStore.setAuthTokens(sessionData.value);

        // Если вошли через ЛК контрагента, то сетим app state(EAppState) вручную на "authenticated"
        if (wasAuthBy.value === EWasAuthBy.entryCode) {
          appStore.setAppState(EAppState.authenticated);
          return sessionData.value;
        }

        // Если вошли любым другим способом, то запрашиваем данные пользователя и сетим нужный app state(EAppState)
        // в зависимости от данных юзера
        try {
          await loadUser(true);
        } catch (error: unknown) {
          appStore.$reset();
          throw error;
        }

        return sessionData.value;
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    };

    /** Войти из-под сотрудника в рамках сессии временного доступа */
    const impersonateByTemporaryAccessSession = async (tokenData: TSessionWithTokensSerialized) => {
      /**
       * Устанавливаем признак смены аккаунта, чтобы корректно отработал переход от одного аккаунта к другому.
       *
       * В логике ниже, сначала токены сбрасываются и после устанавливаются новые, но другие вкладки перехватят сначала пустой токен
       * и так же вызовут у себя RESET_AUTH_STATE. Это приведет к появлению события storage (с пустым токеном) во вкладке, которая инициировала переход из аккаунта в аккаунт.
       * По итогу произойдет следующее: сброс токена -> установка токена -> сброс токена.
       */
      const appStore = useAppStore();
      appStore.setTemporaryAccessSessionSwitchingState(true);
      appStore.$reset();

      return proceedSignIn(tokenData, EWasAuthBy.temporaryAccessSession)
        .finally(() => {
          appStore.setTemporaryAccessSessionSwitchingState(false);
        });
    };

    /** Вернуться в свой аккаунт после входа из-под сотрудника в рамках сессии временного доступа */
    const reauthenticateAfterTemporaryAccessSession = async () => {
      try {
        const newTokenData = await reauthenticateUser();
        if (!newTokenData) throw new Error('No session data');

        const appStore = useAppStore();
        appStore.setTemporaryAccessSessionSwitchingState(true);
        appStore.$reset();
        return proceedSignIn(newTokenData, EWasAuthBy.loginPassword)
          .finally(() => {
            appStore.setTemporaryAccessSessionSwitchingState(false);
          });
      } catch (error) {
        useAppStore().$reset();
        showErrorNotification(error as TErrorNotification);
        return null;
      }
    };

    /** Войти в систему */
    const signIn = async (payload: TAuthenticateUserPayload | TAuthenticateContractorUserPayload) => {
      try {
        const tokenData = await authenticateUser(payload);

        if (!tokenData?.accessToken) throw new Error('No access token');
        decodedAccessToken.value = decodeJWT(tokenData.accessToken);

        // Если был вход в кабинет контрагента, то тогда в токене содержится информация об entry_uid
        if (decodedAccessToken.value?.entry_uid) {
          return proceedSignIn(tokenData, EWasAuthBy.entryCode);
        }

        // Во всех остальных ситуациях ожидаем, что вход был через логин-пароль-(код компании) и ориентируемся на наличие
        // user_id внутри токена
        if (!decodedAccessToken.value?.user_id) return Promise.reject();

        return proceedSignIn(tokenData, EWasAuthBy.loginPassword);
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    };

    /** Войти в систему через соц. сети */
    const signInBySocial = async (payload: TSessionWithTokensSerialized) => {
      if (!payload.accessToken) return;

      await proceedSignIn(payload, EWasAuthBy.social);
    };

    /** Зарегистрировать пользователя */
    const signUp = async (
      payload: TRegisterUserPayload,
      isSignUpBySocial: boolean,
    ) => {
      try {
        sessionData.value = isSignUpBySocial
          ? await registerUserBySberBusinessAccount(payload)
          : await registerUser(payload);
        if (!sessionData.value?.accessToken) return null;

        const appStore = useAppStore();
        if (isSignUpBySocial) {
          appStore.setWasAuthBy(EWasAuthBy.social);
        }
        appStore.setAuthTokens(sessionData.value);
        try {
          await loadUser(true);
        } catch (error: unknown) {
          appStore.$reset();
          throw error;
        }

        return sessionData.value;
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    };

    const $reset = () => {
      user.value = null;
      sessionData.value = null;
      decodedAccessToken.value = null;
      wasAuthBy.value = null;
    };

    return {
      isUserLoading,
      user,
      decodedAccessToken,
      wasAuthBy,

      loadUser,
      impersonateByTemporaryAccessSession,
      reauthenticateAfterTemporaryAccessSession,
      signIn,
      signInBySocial,
      signUp,
      $reset,
    };
  });
